// Treasury fiat-transactions module endpoint functions.
//
// Endpoints accept camelCase view-model inputs and return camelCase view-model
// outputs. Snake_case lives ONLY between the network and the parser.

import { adminApi } from '@core/api/httpClient'
import { FIAT_TRANSACTIONS_ENDPOINT_PATHS } from './fiatTransactionsEndpointPaths'
import type {
  FiatTransactionDetailDto,
  FiatTransactionsListResponseDto,
} from './fiatTransactionsContracts'
import type {
  FiatTransactionDetail,
  FiatTransactionsListResponse,
  GetFiatTransactionsParams,
} from '../types/fiatTransactionsTypes'
import {
  parseFiatTransactionDetail,
  parseFiatTransactionsListResponse,
} from './fiatTransactionsParser'

type WireListResponse = FiatTransactionsListResponseDto | { data: FiatTransactionsListResponseDto }
type WireDetailResponse = FiatTransactionDetailDto | { data: FiatTransactionDetailDto }

function unwrap<T>(payload: T | { data: T }): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

function buildQueryParams(params: GetFiatTransactionsParams): Record<string, string> {
  const queryParams: Record<string, string> = {}
  if (params.page) queryParams.page = String(params.page)
  if (params.limit) queryParams.limit = String(params.limit)
  if (params.treasuryAccountId) queryParams.treasuryAccountId = params.treasuryAccountId
  if (params.investorId) queryParams.investorId = params.investorId
  if (params.investorBankAccountId) queryParams.investorBankAccountId = params.investorBankAccountId
  if (params.investorName) queryParams.investorName = params.investorName
  if (params.investorEmail) queryParams.investorEmail = params.investorEmail
  if (params.status) queryParams.status = params.status
  if (params.referenceType) queryParams.referenceType = params.referenceType
  if (params.transactionType) queryParams.transactionType = params.transactionType
  if (params.transactionReason) queryParams.transactionReason = params.transactionReason
  if (params.fromDate) queryParams.fromDate = params.fromDate
  if (params.toDate) queryParams.toDate = params.toDate
  if (params.amountMin) queryParams.amountMin = params.amountMin
  if (params.amountMax) queryParams.amountMax = params.amountMax
  if (params.sortOrder) queryParams.sortOrder = params.sortOrder
  return queryParams
}

export async function getFiatTransactions(
  params: GetFiatTransactionsParams,
): Promise<FiatTransactionsListResponse> {
  const { data } = await adminApi.get<WireListResponse>(FIAT_TRANSACTIONS_ENDPOINT_PATHS.LIST, {
    params: buildQueryParams(params),
  })
  return parseFiatTransactionsListResponse(unwrap(data))
}

export async function getFiatTransactionDetail(id: string): Promise<FiatTransactionDetail> {
  const { data } = await adminApi.get<WireDetailResponse>(
    FIAT_TRANSACTIONS_ENDPOINT_PATHS.DETAIL(id),
  )
  return parseFiatTransactionDetail(unwrap(data))
}

/**
 * Hits the CSV download endpoint with the current filter params and returns
 * the binary response as a Blob. The container hands the blob to
 * `downloadBlob()` (from `@shared/utils`) to fire the browser download.
 *
 * The list and download endpoints share the same query-param surface — the
 * download endpoint just streams every matching row (with a 50k safety cap
 * enforced server-side via `FIAT_TRANSACTIONS_DOWNLOAD_LIMIT_EXCEEDED`).
 */
export async function downloadFiatTransactions(params: GetFiatTransactionsParams): Promise<Blob> {
  // Drop pagination — download is unpaginated by contract.
  const downloadParams: GetFiatTransactionsParams = { ...params }
  delete downloadParams.page
  delete downloadParams.limit
  const { data } = await adminApi.get<Blob>(FIAT_TRANSACTIONS_ENDPOINT_PATHS.DOWNLOAD, {
    params: buildQueryParams(downloadParams),
    responseType: 'blob',
  })
  return data
}
